"use client";
import type { ImageProps } from "next/image";
import Image from "next/image";
import { imageUrlFor } from "./imageBuilder";
import { ImageAssetQueryResult } from "../sanity.types";
import { ImageReference } from "./SanityImage";
import { useMemo } from "react";
import {
  getSanityImageDimensions,
  shouldPreserveSvg,
} from "./getSanityImageDimensions";

export type SanityAssetImageProps = {
  image: ImageReference;
  asset?: ImageAssetQueryResult;
  aspectRatio?: number;
  /** Deliver the image cropped to `aspectRatio` around the hotspot, instead of only sizing the box */
  cropToAspectRatio?: boolean;
} & Omit<ImageProps, "src" | "alt" | "height" | "width"> &
  Pick<Partial<ImageProps>, "alt" | "width" | "height">;

export function SanityAssetImage({
  image,
  asset,
  aspectRatio,
  cropToAspectRatio = false,
  width,
  height,
  fill,
  ...rest
}: SanityAssetImageProps) {
  const dimensions = useMemo(
    () =>
      getSanityImageDimensions({
        assetDimensions: asset?.metadata?.dimensions,
        crop: image.crop,
        aspectRatio,
        width,
        height,
        fill,
      }),
    [aspectRatio, asset?.metadata?.dimensions, image.crop, fill, height, width],
  );

  if (!asset) return null;

  const imageBuilder = imageUrlFor({ ...image, ...asset });

  // An explicit crop lets Sanity rasterise the SVG around the hotspot
  if (
    shouldPreserveSvg(asset.extension, image.crop) &&
    !(cropToAspectRatio && aspectRatio)
  ) {
    return (
      <Image
        blurDataURL={asset.metadata?.lqip}
        placeholder="blur"
        src={imageBuilder.url()}
        alt={asset.altText}
        {...dimensions}
        {...rest}
      />
    );
  }

  return (
    <Image
      loader={({ width: w, quality }) => {
        const builder = imageBuilder.withOptions({
          format: "webp",
          quality,
          width: w,
        });
        if (!cropToAspectRatio || !aspectRatio) return builder.url();
        return builder
          .height(Math.round(w / aspectRatio))
          .fit("crop")
          .url();
      }}
      src={imageBuilder.format("webp").url()}
      alt={asset.altText ?? "Misssing alt text"}
      placeholder="blur"
      blurDataURL={asset.metadata?.lqip}
      {...dimensions}
      {...rest}
    />
  );
}
